import {
  Button,
  DatePicker,
  FormErrorSummary,
  Modal,
  StatusText,
  Textarea,
  TimePicker,
  formatCurrency,
  useSnackbar,
  type CivilDate,
} from '@hue/design-system';
import { useQueryClient } from '@tanstack/react-query';
import { HandCoins } from 'lucide-react';
import { useState } from 'react';
import { RequestAlert } from '../../app/data-view.js';
import { SectionCardHeader } from '../../app/section-card-header.js';
import { getApiError, schedulingApi, type Appointment } from '../../lib/api.js';
import { useNow } from '../../lib/use-now.js';
import { depositStateOf } from './deposit-state.js';
import { civilDateOf, formatDate, formatHourMinute } from '../../lib/time-zone.js';

function dueText(appointment: Appointment, timeZone: string): string {
  if (!appointment.depositDueAt) return '';
  return ` até ${formatDate(appointment.depositDueAt, timeZone)} às ${formatHourMinute(
    appointment.depositDueAt,
    timeZone,
  )}`;
}

/// Uma linha sob a situação do pedido, na lista e no calendário.
export function AppointmentDepositNotice({
  appointment,
  timeZone,
  now,
}: {
  appointment: Appointment;
  timeZone: string;
  now?: number | undefined;
}) {
  const clock = useNow();
  const state = depositStateOf(appointment, now ?? clock);
  if (!state) return null;
  const zone = appointment.timeZone ?? timeZone;
  const amount = formatCurrency(appointment.depositAmountCents!, appointment.currency);
  return (
    <p className="mt-1 text-xs">
      {state === 'PAID' ? (
        <StatusText tone="positive">Sinal pago · {amount}</StatusText>
      ) : state === 'WAIVED' ? (
        <StatusText tone="neutral">Sinal dispensado</StatusText>
      ) : state === 'PENDING' ? (
        <StatusText tone="warning">
          Sinal pendente · {amount}
          {dueText(appointment, zone)}
        </StatusText>
      ) : state === 'EXPIRED' ? (
        <StatusText tone="danger">Prazo do sinal vencido · {amount}</StatusText>
      ) : (
        <StatusText tone="danger">Pedido depois do prazo · ajuste o prazo</StatusText>
      )}
    </p>
  );
}

/// O sinal na página do atendimento: valor, quanto já entrou, prazo e as ações da recepção —
/// ajustar o prazo ou dispensar com motivo. Pagar acontece na venda do atendimento, ao lado.
export function AppointmentDepositCard({
  appointment,
  timeZone,
  today,
  canManage,
  now: fixedNow,
}: {
  appointment: Appointment;
  timeZone: string;
  today: CivilDate;
  canManage: boolean;
  now?: number | undefined;
}) {
  const clock = useNow();
  const now = fixedNow ?? clock;
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [dialog, setDialog] = useState<'waive' | 'due' | null>(null);
  const [reason, setReason] = useState('');
  const [date, setDate] = useState<string | null>(null);
  const [time, setTime] = useState<string | null>(null);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const state = depositStateOf(appointment, now);
  if (!state) return null;
  const zone = appointment.timeZone ?? timeZone;
  const amount = appointment.depositAmountCents!;
  const paid = appointment.depositPaidCents ?? 0;
  const open = state !== 'PAID' && state !== 'WAIVED';

  function show(kind: 'waive' | 'due') {
    setError(undefined);
    setReason('');
    setDate(civilDateOf(new Date(now + 86_400_000), zone));
    setTime('18:00');
    setDialog(kind);
  }

  async function run(action: () => Promise<Appointment>, message: string) {
    setBusy(true);
    setError(undefined);
    try {
      await action();
      await queryClient.invalidateQueries({ queryKey: ['company', 'scheduling'] });
      setDialog(null);
      snackbar.success(message);
    } catch (failure) {
      setError(getApiError(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-2xl border border-surface-border bg-surface-card shadow-soft">
      <SectionCardHeader title="Sinal do agendamento" icon={HandCoins} />
      <div className="grid gap-2 p-4 text-sm">
        <AppointmentDepositNotice appointment={appointment} timeZone={timeZone} now={now} />
        <p>
          Sinal de {formatCurrency(amount, appointment.currency)} · pago{' '}
          {formatCurrency(Math.min(paid, amount), appointment.currency)}
          {dueText(appointment, zone)}.
        </p>
        {state === 'WAIVED' ? (
          <p className="text-content-muted">
            Dispensado por {appointment.depositWaivedByName}: {appointment.depositWaivedReason}
          </p>
        ) : state === 'PAID' ? (
          <p className="text-content-muted">
            O sinal foi pago. A confirmação continua sendo decisão da unidade.
          </p>
        ) : (
          <p className="text-content-muted">
            Registre o sinal em Pagamentos da venda do atendimento. Sem o sinal pago, a unidade só
            confirma dispensando-o com motivo.
          </p>
        )}
        {open && canManage ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => show('due')}>
              Ajustar prazo
            </Button>
            <Button variant="outline" onClick={() => show('waive')}>
              Dispensar sinal
            </Button>
          </div>
        ) : null}
      </div>
      <Modal
        open={dialog === 'waive'}
        onOpenChange={() => setDialog(null)}
        title="Dispensar sinal"
        footer={
          <>
            <FormErrorSummary form="deposit-waiver" />
            <Button type="submit" form="deposit-waiver" loading={busy}>
              Dispensar sinal
            </Button>
          </>
        }
      >
        <form
          id="deposit-waiver"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (reason.trim().length < 3) {
              setError('Informe o motivo da dispensa.');
              return;
            }
            void run(
              () => schedulingApi.waiveDeposit(appointment.id, reason),
              'Sinal dispensado. Confirme o horário quando a unidade decidir.',
            );
          }}
        >
          <p className="mb-4 text-sm">
            A dispensa vale só para este pedido e fica registrada com o seu nome. Ela não confirma o
            horário.
          </p>
          <Textarea
            label="Motivo da dispensa"
            aria-invalid={!!error && reason.trim().length < 3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          {error ? <RequestAlert message={error} /> : null}
        </form>
      </Modal>
      <Modal
        open={dialog === 'due'}
        onOpenChange={() => setDialog(null)}
        title="Ajustar prazo do sinal"
        footer={
          <>
            <FormErrorSummary form="deposit-due" />
            <Button type="submit" form="deposit-due" loading={busy}>
              Salvar prazo
            </Button>
          </>
        }
      >
        <form
          id="deposit-due"
          noValidate
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!date || !time) {
              setError('Informe a data e o horário do novo prazo.');
              return;
            }
            void run(
              () => schedulingApi.adjustDepositDue(appointment.id, { date, time }),
              'Prazo do sinal ajustado.',
            );
          }}
        >
          <p className="text-sm">
            Novo limite para o cliente pagar o sinal, no horário da unidade do atendimento.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <DatePicker label="Data do prazo" value={date} onValueChange={setDate} today={today} />
            <TimePicker label="Horário do prazo" value={time} onValueChange={setTime} />
          </div>
          {error ? <RequestAlert message={error} /> : null}
        </form>
      </Modal>
    </div>
  );
}
